import { all, delay, put, takeLatest } from 'redux-saga/effects';
import {
  GET_TTS_USER_RETRY_FIRST_DELAY,
  GET_TTS_USER_RETRY_MAX_DELAY,
} from '@src/constants';
import { HAVE_NO_ORDER, ORDER_STATUS } from '@src/constants/order';
import { PACKAGE_STATUS, PACKAGE_TYPE } from '@src/constants/package';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import { ERROR_CODE } from '@src/errors/code';
import apis from '@src/apis';
import actions from '../actions';

function* getUsingPackageSaga({ userId }) {
  if (!userId) return;
  try {
    const { result: dubbingResult } = yield apis.orders.getOrders({
      userId,
      packageStatus: PACKAGE_STATUS.USING,
      type: PACKAGE_TYPE.DUBBING,
      limit: 1,
    });

    const dubbingPackage = dubbingResult?.orders[0]?.package;
    yield put(actions.user.getUsingDubbingPackageSuccess(dubbingPackage));
  } catch (error) {
    yield put(actions.user.getUsingPackageFailure());
  }
}

function* getLatestOrderSaga({ userId }) {
  try {
    const { status, result } = yield apis.orders.getOrders({
      userId,
      type: PACKAGE_TYPE.STUDIO,
      sort: 'createdAt_desc',
      limit: 10,
    });
    if (!status) throw new Error();
    const latestOrder = result.orders[0] || { status: HAVE_NO_ORDER };
    const latestOrderByUser = result.orders?.find(
      (item) =>
        item?.paymentMethod?.type !== PAYMENT_METHOD_TYPE.ADMIN &&
        item?.paymentMethod?.type !== PAYMENT_METHOD_TYPE.GOOGLE &&
        item?.paymentMethod?.type !== PAYMENT_METHOD_TYPE.APPLE &&
        item?.status === ORDER_STATUS.PAID,
    );
    yield put(actions.user.getLatestOrderSuccess(latestOrder));
    yield put(actions.user.getLatestOrderSuccessByUser(latestOrderByUser));

    if (latestOrder.status === ORDER_STATUS.PENDING && latestOrder.voucher)
      yield put(actions.voucher.applyVoucher(latestOrder.voucher));
  } catch (error) {
    yield put(actions.user.getLatestOrderFailure());
  }
}

function* getLatestDubbingOrderSaga({ userId }) {
  try {
    const { status, result } = yield apis.orders.getOrders({
      userId,
      type: PACKAGE_TYPE.DUBBING,
      sort: 'createdAt_desc',
      limit: 1,
    });
    if (!status) throw new Error();
    const latestOrder = result.orders[0] || { status: HAVE_NO_ORDER };
    yield put(actions.user.getLatestDubbingOrderSuccess(latestOrder));

    if (latestOrder.status === ORDER_STATUS.PENDING && latestOrder.voucher)
      yield put(actions.voucher.applyVoucher(latestOrder.voucher));
  } catch (error) {
    yield put(actions.user.getLatestDubbingOrderFailure());
  }
}

function* getLastExpiredOrderSaga() {
  try {
    const { status, result } = yield apis.orders.getLastExpiredOrder();
    if (!status) throw new Error();
    yield put(actions.user.getLastExpiredOrderSuccess(result));
  } catch (error) {
    yield put(actions.user.getLatestOrderFailure());
  }
}

function* fetchTtsUser(
  previousDelay = GET_TTS_USER_RETRY_FIRST_DELAY,
  nextDelay = GET_TTS_USER_RETRY_FIRST_DELAY,
) {
  try {
    const { status, result } = yield apis.user.getTtsUser();
    if (!status) throw new Error();
    return result;
  } catch (error) {
    const isServerError =
      error?.response?.status >= ERROR_CODE.INTERNAL_SERVER_ERROR;
    if (!isServerError || nextDelay > GET_TTS_USER_RETRY_MAX_DELAY) throw error;
    yield delay(nextDelay);
    return yield* fetchTtsUser(nextDelay, previousDelay + nextDelay);
  }
}

function* getTtsUserSaga() {
  let ttsUser;

  try {
    ttsUser = yield* fetchTtsUser();
  } catch (error) {
    yield put(actions.user.getTtsUserFailure());
    return;
  }

  yield put(actions.user.getTtsUserSuccess(ttsUser));

  const { packageCode } = ttsUser || {};
  if (!packageCode) return;

  try {
    const { status, result } = yield apis.packages.getPackages({
      code: packageCode,
    });
    if (!status || !result?.packages?.length) throw new Error();
    yield put(actions.user.getUsingPackageSuccess(result.packages[0]));
  } catch (error) {
    yield put(actions.user.getTtsUserFailure());
  }
}

function* getTrialVoiceUsageSaga() {
  try {
    const { status, result } = yield apis.user.getTrialVoiceUsage();
    if (!status) throw new Error();
    yield put(actions.user.updateTrialVoiceUsage(result?.voiceUsage));
  } catch (error) {
    yield put(actions.user.getTrialVoiceUsageFailure());
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actions.user.actionTypes.GET_USING_PACKAGE, getUsingPackageSaga),
    takeLatest(actions.user.actionTypes.GET_LATEST_ORDER, getLatestOrderSaga),
    takeLatest(
      actions.user.actionTypes.GET_LATEST_DUBBING_ORDER,
      getLatestDubbingOrderSaga,
    ),
    takeLatest(
      actions.user.actionTypes.GET_LAST_EXPIRED_ORDER,
      getLastExpiredOrderSaga,
    ),
    takeLatest(actions.user.actionTypes.GET_TTS_USER, getTtsUserSaga),
    takeLatest(
      actions.user.actionTypes.GET_TRIAL_VOICE_USAGE,
      getTrialVoiceUsageSaga,
    ),
  ]);
}
